import { LoggerHelper } from '@src/core';
import { useEffect, useState } from 'react';

/** O evento não está na lib do DOM: é proposta só implementada em navegadores Chromium. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UsePwaInstallReturn {
  isInstallable: boolean;
  isStandalone: boolean;
  canPrompt: boolean;
  promptInstall: () => Promise<void>;
}

/**
 * Estado de instalação do app como PWA.
 *
 * O navegador só deixa abrir o diálogo nativo a partir do evento
 * `beforeinstallprompt`, e só o entrega uma vez -- por isso ele é guardado em
 * vez de consumido na hora. iOS não dispara esse evento: lá a instalação é
 * manual, e o que resta é instruir.
 */
const usePwaInstall = (): UsePwaInstallReturn => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  /**
   * Já instalado: a janela abre sem a interface do navegador.
   *
   * São três checagens porque cada plataforma responde de um jeito -- a media
   * query no Android, `navigator.standalone` no iOS e o referrer quando a
   * abertura veio do atalho na tela inicial.
   */
  const isStandalone = Boolean(
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone ||
    document.referrer.includes('android-app://')
  );

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      // Sem o preventDefault o navegador mostra o próprio banner, e o convite
      // do app apareceria junto, duplicado.
      event.preventDefault();

      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<void> => {
    if (deferredPrompt === null) {
      LoggerHelper.error('Convite de instalação pedido sem evento do navegador disponível');
      return;
    }

    await deferredPrompt.prompt();

    const { outcome } = await deferredPrompt.userChoice;
    LoggerHelper.log(`Convite de instalação: ${outcome}`);

    // O evento serve uma vez só; guardá-lo depois disso não adianta.
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  return {
    isInstallable,
    isStandalone,
    canPrompt: deferredPrompt !== null,
    promptInstall,
  };
};

export { usePwaInstall };
